import React from "react";
import type { ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { cn } from "~/lib/utils";
import { trpc } from "~/lib/trpc";
import { useT, supportedLocales, type Locale } from "~/lib/i18n";

/**
 * The product shell — small header, no sidebar, full-width main area.
 *
 * No top banner, no announcement bar, no notification badge bait.
 */
export function AppShell({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <div className="min-h-dvh flex flex-col bg-surface-page text-ink-primary">
      <Header />
      <main className="flex-1 w-full">{children}</main>
      <Footer />
    </div>
  );
}

function Header(): React.JSX.Element {
  const { t } = useT();
  const whoamiQ = trpc.me.whoami.useQuery(undefined, { retry: false, staleTime: 60_000 });
  const user = whoamiQ.data?.user ?? null;

  return (
    <header
      className={cn(
        "sticky top-0 z-20 h-12 flex items-center",
        "border-b border-[var(--divider-weak)]",
        "bg-[color-mix(in_oklab,var(--surface-page)_80%,transparent)] backdrop-blur",
      )}
    >
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-5 flex items-center gap-3 sm:gap-6">
        <Link to="/" className="flex items-center gap-2 group shrink-0">
          <Logomark className="h-5 w-5 text-ink-primary" />
          <span className="font-display italic text-[15px] tracking-tight text-ink-primary hidden sm:inline">MotorScan</span>
        </Link>
        <nav className="flex items-center gap-0.5 sm:gap-1 text-[12px] sm:text-[13px]">
          <NavItem to="/scan/new">{t("nav.newScan")}</NavItem>
          <NavItem to="/scans">{t("nav.scans")}</NavItem>
          <NavItem to="/marche">{t("nav.market")}</NavItem>
        </nav>
        <div className="ml-auto flex items-center gap-2 sm:gap-3 text-[12px] sm:text-[13px]">
          <LocaleSwitcher />
          {user ? (
            <>
              <NavItem to="/account">{user.email.split("@")[0]}</NavItem>
              <form method="POST" action="/auth/logout" className="contents">
                <button type="submit" className="h-8 px-2 sm:px-2.5 rounded-[3px] text-ink-tertiary hover:text-ink-secondary hover:bg-surface-hover transition-colors duration-[var(--dur-quick)]">
                  {t("common.signout")}
                </button>
              </form>
            </>
          ) : (
            <Link
              to="/login"
              className="h-8 px-3 rounded-[3px] inline-flex items-center bg-[var(--accent)] text-[var(--ink-on-accent)] font-medium hover:bg-[var(--accent-hover)] transition-colors duration-[var(--dur-quick)]"
            >
              {t("common.signin")}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

function LocaleSwitcher(): React.JSX.Element {
  const { locale, setLocale } = useT();
  // setLocale mutation persists the choice on the user record (no-op for
  // anonymous users — the mutation will 401 silently). The local state is
  // updated immediately regardless.
  const setLocaleMutation = trpc.me.setLocale.useMutation();
  return (
    <div className="flex items-center gap-1 mono text-[11px] uppercase tracking-[0.08em] text-ink-tertiary">
      {supportedLocales.map((l, i) => (
        <React.Fragment key={l}>
          {i > 0 && <span aria-hidden="true">·</span>}
          <button
            type="button"
            onClick={() => {
              setLocale(l as Locale);
              setLocaleMutation.mutate({ locale: l }, { onError: () => undefined });
            }}
            aria-current={l === locale ? "true" : undefined}
            className={cn(
              "px-0.5 transition-colors duration-[var(--dur-quick)]",
              l === locale ? "text-ink-primary" : "hover:text-ink-secondary cursor-pointer",
            )}
          >
            {l}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}

function NavItem({ to, children }: { to: string; children: ReactNode }): React.JSX.Element {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "h-8 px-2.5 rounded-[3px] inline-flex items-center",
          "transition-colors duration-[var(--dur-quick)] ease-[var(--ease-out)]",
          "hover:bg-surface-hover",
          isActive ? "text-ink-primary font-medium" : "text-ink-secondary",
        )
      }
    >
      {children}
    </NavLink>
  );
}

function Footer(): React.JSX.Element {
  return (
    <footer className="border-t border-[var(--divider-weak)] py-6">
      <div className="w-full max-w-[1440px] mx-auto px-5 flex items-center justify-between text-[12px] text-ink-tertiary">
        <span>MotorScan © {new Date().getFullYear()}</span>
        <span className="mono">v0 · internal beta</span>
      </div>
    </footer>
  );
}

function Logomark({ className }: { className?: string }): React.JSX.Element {
  // Minimal single-line car silhouette — designed to read at 16-24px.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M3 14l1.5-4a2 2 0 0 1 1.9-1.4h11.2a2 2 0 0 1 1.9 1.4L21 14" />
      <path d="M3 14h18v3a1 1 0 0 1-1 1h-1a2 2 0 0 1-4 0H9a2 2 0 0 1-4 0H4a1 1 0 0 1-1-1z" />
    </svg>
  );
}
